import type { ApiResponse, DashboardData, TracerouteData, PingHistoryData, SchedulerStatus } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    console.log(`🚀 API 요청 시작: ${url}`)
    
    // AbortController를 사용한 타임아웃 설정 (20초)
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 20000)
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options?.headers,
        },
        credentials: 'same-origin',
        signal: controller.signal,
        ...options,
      })

      clearTimeout(timeoutId)
      console.log(`📡 응답 받음: ${response.status} ${response.statusText}`)
      console.log(`📍 응답 URL: ${response.url}`)
      console.log(`🔍 응답 헤더:`, Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`❌ HTTP 에러: ${response.status} ${response.statusText}`, errorText)
        throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text()
        console.error(`❌ 잘못된 Content-Type: ${contentType}`, text)
        throw new Error(`Expected JSON response but got: ${contentType}`)
      }

      const data = await response.json()
      console.log(`✅ 데이터 파싱 성공:`, data)
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      console.error(`💥 API 요청 실패 (${endpoint}):`, error)
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.')
        }
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          throw new Error('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.')
        }
      }
      throw error
    }
  }

  // 대시보드 데이터 조회
  async getDashboardData(): Promise<ApiResponse<DashboardData>> {
    console.log('API 요청 시작:', `${API_BASE_URL}/monitoring/dashboard`)
    try {
      const result = await this.request<ApiResponse<DashboardData>>('/monitoring/dashboard')
      console.log('API 응답 데이터:', result)
      return result
    } catch (error) {
      console.error('API 요청 에러:', error)
      throw error
    }
  }

  // 특정 국가의 traceroute 결과 조회
  async getTracerouteData(country: string): Promise<ApiResponse<TracerouteData>> {
    return this.request<ApiResponse<TracerouteData>>(`/monitoring/traceroute/${encodeURIComponent(country)}`)
  }

  // 특정 국가의 ping 히스토리 조회
  async getPingHistory(country: string, hours: number = 24): Promise<ApiResponse<PingHistoryData>> {
    return this.request<ApiResponse<PingHistoryData>>(`/monitoring/ping-history/${encodeURIComponent(country)}?hours=${hours}`)
  }

  // 스케줄러 상태 조회
  async getSchedulerStatus(): Promise<ApiResponse<SchedulerStatus>> {
    return this.request<ApiResponse<SchedulerStatus>>('/monitoring/scheduler/status')
  }

  // 수동으로 ping 테스트 실행
  async runPingTests(): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>('/monitoring/ping/run', {
      method: 'POST',
    })
  }

  // 수동으로 traceroute 테스트 실행
  async runTracerouteTests(): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>('/monitoring/traceroute/run', {
      method: 'POST',
    })
  }
}

export const apiService = new ApiService()
export default apiService